// src/components/reports/MeetingRoadmapSection.jsx
//
// Recommendations & Roadmap, at the foot of the monthly tech-catchup Meeting
// Notes — below Talking Points.
//
// ⚠ CLIENT-VISIBLE CONTENT, INTERNAL CONTROL. The section itself is shown to the
// client during the meeting: items, timing, cost, status, quote-state. The
// "Create Quote" button is NOT — it is gated on `internal`, so the client-facing
// meeting page (public/meeting.html) renders the same rows with badges and no
// buttons. Defaulting `internal` to FALSE is deliberate: a surface that forgets
// to pass it shows the safe thing.
//
// ⛔ FROZEN SET, LIVE QUOTE-STATE. The rows are whatever the roadmap held when
// the notes were generated; only quote-state is read live. See meetingRoadmap.js
// for why — freezing it would make the button appear to do nothing.
import { useState, useEffect, useCallback } from "react";
import { getRoadmap } from "../../api/roadmapApi";
import { mergeLiveQuoteState, groupForMeeting, statusLabel } from "./meetingRoadmap";
import { quoteStateView, quotedLabel, isQuoted } from "./quoteState";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
/* Built from the string, never `new Date("2026-11")` — that parses as UTC
   midnight and prints the previous month in Perth. */
const monthLabel = (p) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(p || ""));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "Timing to be confirmed";
};
const money = (n) =>
  Number(n) > 0 ? `$${Math.round(Number(n)).toLocaleString("en-AU")}` : "";

const chip = (bg, fg) => ({
  fontSize: 9.5, fontWeight: 800, letterSpacing: .4, textTransform: "uppercase",
  padding: "2px 7px", borderRadius: 20, background: bg, color: fg, whiteSpace: "nowrap",
});

export default function MeetingRoadmapSection({
  snapshot,               // frozen rows stored on the meeting's brief
  companyId = null,       // internal surface: whose live quote-state to fetch
  liveRoadmap = null,     // public surface: quote-state supplied by the server
  internal = false,       // ⛔ buttons ONLY when true
  onCreateQuote = null,   // (item) => Promise — the ONE shared action
}) {
  const [pending, setPending] = useState(null);
  const [fetched, setFetched] = useState(null);

  /* ⚠ THE LIVE HALF IS FETCHED HERE, not threaded down from the Meeting Notes
     tab — that lives ~9,000 lines into MspOsApp and adding hooks around it is
     how this file grows another bug. The public meeting page cannot call an
     authenticated route, so it passes `liveRoadmap` in instead, enriched by the
     server from the token's own companyId. */
  const load = useCallback(() => {
    if (!internal || !companyId) return;
    getRoadmap(companyId).then(setFetched).catch(() => setFetched(null));
  }, [internal, companyId]);
  useEffect(load, [load]);

  if (!snapshot || !(snapshot.items || []).length) return null;

  const merged = mergeLiveQuoteState(snapshot, liveRoadmap || fetched);
  const g = groupForMeeting(merged.items);

  const fire = async (item) => {
    if (!onCreateQuote) return;
    /* ⚠ CONFIRMED — this creates a quote AND emails Craig, and it is clickable
       during a live meeting where a mis-click is expensive. */
    if (!window.confirm(`Create a quote for "${item.title}" and notify Craig?`)) return;
    setPending(item.id);
    /* Reload after either outcome: on success the badge must flip to "Quoted",
       and on the idempotent path the badge is what the user was missing. */
    try { await onCreateQuote(item); } finally { setPending(null); load(); }
  };

  const Row = (item) => {
    const v = quoteStateView(item.quoteState);
    const quoted = isQuoted(item.quoteState);
    return (
      <div key={item.id} style={{
        borderTop: "1px solid var(--border,#e6e6e6)", padding: "9px 0",
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{item.title}</div>
          {item.detail && (
            <div style={{ fontSize: 11.5, color: "var(--text3,#666)", marginTop: 2 }}>{item.detail}</div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 6 }}>
            {/* ⛔ BOTH AXES, side by side — "Budgeted · Quoted". */}
            <span style={chip("#f4f4f4", "#555")}>{statusLabel(item)}</span>
            <span style={chip(v.bg, v.fg)}>{quotedLabel(item)}</span>
            <span style={{ fontSize: 11.5, color: "var(--text3,#666)" }}>{monthLabel(item.targetMonth)}</span>
            {money(item.cost) && (
              <span style={{ fontSize: 12, fontWeight: 700 }}>{money(item.cost)}</span>
            )}
          </div>
        </div>
        {/* ⛔ Internal only. Once quoted the button is gone — the badge above
            already says so, and a second live button invites the duplicate the
            server would refuse, which reads as the button being broken. */}
        {internal && !quoted && onCreateQuote && (
          <button className="ico-btn" style={{ flexShrink: 0, fontSize: 11, padding: "4px 9px" }}
            disabled={pending === item.id}
            onClick={() => fire(item)}
            title="Creates a quote pre-filled from this item and emails Craig.">
            {pending === item.id ? "…" : "🧾 Create Quote"}
          </button>
        )}
      </div>
    );
  };

  const Group = (title, note, rows) => (rows.length ? (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800 }}>{title}</div>
      {note && <div style={{ fontSize: 11, color: "var(--text3,#666)", marginTop: 2 }}>{note}</div>}
      {rows.map(Row)}
    </div>
  ) : null);

  return (
    <div className="mip-card" style={{ gridColumn: "1/-1" }}>
      <div className="mip-card-hdr">🗺️ Recommendations &amp; Roadmap</div>
      <div className="mip-card-body">
        {/* Scheduled work is grouped by month in the report; here the month sits
            on each row, because a meeting reads as one list. */}
        {Group("Scheduled projects", "Work we have booked in with you.", g.scheduled)}
        {Group("Budgeted, not yet scheduled", "Approved in your technology budget — we'll agree timing.", g.budgeted)}
        {Group("Quick wins", "Small pieces of work with a quick payoff.", g.quickWins)}
        {Group("Worth considering", "Not in the current budget — raised so you can weigh them up.", g.recommended)}
        {merged.isolationOk === false && internal && (
          <div style={{ fontSize: 11, color: "#a32a24", marginTop: 10 }}>
            ⚠ Quote status hidden — the loaded roadmap is for a different client.
          </div>
        )}
      </div>
    </div>
  );
}
